<template>
  <section class="content-group -column -center -bg-02 -vs-small -no-spacer">
    <div class="group -first -full -bg-07 -vs-small -hs-medium -flex -between" aria-live="polite">
      <h1 ref="sectionHeader" class="section-header" tabindex="-1">
        {{ sectionTitle }}
      </h1>
    </div>
    <!-- multi-step form process.. step 1 billing -->
    <div
      v-show="step === 'billing'"
      class="group -last -full -bg-01 -vs-small -hs-medium -flex -center"
    >
      <billing-address-form
        :initial-billing-address="initialBillingAddress"
        :auto-submit="!!initialBillingAddress"
        action-type="cart"
        @billing-cancel-action="handleBillingCancel"
        @billing-submit-action="handleBillingSubmit"
      />
    </div>

    <!-- step 2 payment -->
    <div
      v-show="step === 'payment'"
      class="group -last -full -bg-01 -vs-small -hs-medium -flex -center"
    >
      <div class="payment-info">
        <!-- billing review -->
        <billing-address-review
          v-if="billingAddress"
          :three-d-secure-billing-address="billingAddress.threeDSecure"
          @billing-back-action="goToBillingStep"
        />

        <!-- payment form -->
        <vee-form v-slot="{ validate }" as="">
          <form
            ref="form"
            :action="`/store/process/${purchaseId}`"
            method="post"
            class="payment-details standard-form"
            novalidate
            @submit.prevent="handleSubmit(validate)"
          >
            <!-- wallet options -->
            <div v-if="hasWalletBalance" class="cart-payment-wallet">
              <checkbox-input v-model="formData.useWallet" input-id="useWallet">
                <template #label>
                  <span>
                    Apply FB Wallet Funds
                    <span class="wallet-balance">
                      Current Balance: {{ moneyFormat(walletBalance.toString()) }}
                    </span>
                    <small v-if="cannotUseFullBalance">
                      <br />
                      eGift Cards cannot be purchased using FB Wallet funds.
                      <br />
                      The max amount available for this purchase is
                      {{ moneyFormat(maxWalletAmount) }}.
                    </small>
                  </span>
                </template>
              </checkbox-input>

              <text-input
                v-show="formData.useWallet"
                v-model="formData.walletAmount"
                input-id="walletAmount"
                type="number"
                label="Amount to use from your wallet:"
                placeholder="0.00"
                :rules="`required|max_value:${maxWalletAmount}|min_value:0.01`"
                validate-as="Wallet amount"
                :max="maxWalletAmount"
                min="0"
                @input="restrictWalletAmount"
              />
            </div>

            <!-- braintree payment -->
            <div v-show="hasOrderBalance" class="cart-payment-widget">
              <!-- only shows if wallet payment and balance -->
              <div v-show="formData.useWallet" class="-full -bg-01 -flex -between">
                <div class="bag-total">
                  <h1 class="total-title">Order Balance:</h1>
                </div>
                <div class="bag-total-price">
                  <span class="total-price">{{ moneyFormat(orderBalance) }}</span>
                </div>
              </div>

              <div v-if="paymentInfoError" class="cart-widget-error">
                <callout ref="paymentError" type="error" :closable="false">
                  <div>{{ paymentInfoError }}</div>
                </callout>
              </div>

              <braintree-web-drop-in
                :token="token"
                :total="orderBalance"
                @payment-drop-in-loaded="onDropInLoad"
                @three-d-secure-customer-canceled="customerCanceled"
              />
            </div>

            <input
              id="paymentMethodNonce"
              name="paymentMethodNonce"
              type="hidden"
              :value="formData.nonce"
            />

            <csrf-input />

            <!-- billing address - encoded json -->
            <input
              id="billingAddressEncoded"
              name="billingAddressEncoded"
              type="hidden"
              :value="formData.billingAddressEncoded"
            />

            <div class="group -flex -between -vcenter">
              <a class="cancel-order secondary-action" :href="`/store/cancel/${purchaseId}`"
                >Cancel</a
              >
              <submit-button
                type="submit"
                class="btn -main -large-medium-only no-margin cart-btn"
                :submitting="processing || (hasOrderBalance && !braintreeInstance)"
                label="Place Order"
                :submitting-label="submittingLabel"
                @submit-action="handleSubmit(validate)"
              />
            </div>
          </form>
        </vee-form>
      </div>
    </div>

    <!-- Gift card disclaimer -->
    <div v-if="giftCardDisclaimer || plusPassDisclaimer" class="group narrow">
      <p v-if="giftCardDisclaimer" class="body -tight -note">
        By purchasing an eGift Card, you agree to Fitness Blender’s
        <a href="/page/terms-of-use-agreement#tos9a" target="_blank" rel="noopener noreferrer"
          >Gift Card Terms and Conditions</a
        >, including no refunds.
      </p>

      <p v-if="plusPassDisclaimer" class="body -tight -note">
        By purchasing an FB Plus Pass, you agree to Fitness Blender’s
        <a href="/page/terms-of-use-agreement#tos13a" target="_blank" rel="noopener noreferrer"
          >FB Plus Pass Terms and Conditions</a
        >, including no refunds.
      </p>

      <p v-if="payForwardDisclaimer" class="body -tight -note">
        By purchasing a Pay it Forward, you agree to Fitness Blender’s
        <a href="/page/terms-of-use-agreement#tos9b" target="_blank" rel="noopener noreferrer"
          >Pay it Forward Terms and Conditions</a
        >, including no refunds.
      </p>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { initExtraValidationRules } from '@/utils/forms/validation-rules'
import { Form as VeeForm } from 'vee-validate'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import TextInput from '@/components/forms/text-input.vue'
import SubmitButton from '../buttons/submit-button.vue'
import CsrfInput from '../forms/csrf-input.vue'
import BillingAddressForm from './billing-address-form.vue'
import BillingAddressReview from '@/components/cart/billing-address-review.vue'
import Callout from '../notifications/callout.vue'
import BraintreeWebDropIn from '@/components/forms/braintree-web-drop-in.vue'
import { compareFloats, moneyFormat } from '@/utils/helpers'
import { scrollToElemTop } from '@/utils/scroll-to'

/**
 * Multiple step payment form
 * - step 1: billing address (see billing-address-form.vue)
 * - step 2: payment information (handled here, traditional form submit)
 *
 * On step 1, billing can be "auto submitted"
 * - pass in initial-billing-address (EX: BillingAddress::find())
 * - billing-submit-action event will be emitted as soon as related data and formatting is complete
 *
 * On step 2, user can use any of the following:
 * - 100% FB wallet paid
 * - FB wallet + braintree
 * - 100% braintree paid
 */
export default defineComponent({
  name: 'CheckoutPaymentForm',
  components: {
    BillingAddressReview,
    BillingAddressForm,
    VeeForm,
    CheckboxInput,
    TextInput,
    SubmitButton,
    CsrfInput,
    Callout,
    BraintreeWebDropIn,
  },
  props: {
    // should match BillingAddress model (underscore properties)
    initialBillingAddress: {
      type: Object,
      default: () => null,
    },
    giftCardDisclaimer: {
      type: Boolean,
      default: false,
    },
    plusPassDisclaimer: {
      type: Boolean,
      default: false,
    },
    payForwardDisclaimer: {
      type: Boolean,
      default: false,
    },
    token: {
      type: String,
      required: true,
    },
    purchaseId: {
      type: Number,
      required: true,
    },
    // braintree requires string
    total: {
      type: String,
      required: true,
    },

    // users raw wallet balance
    walletBalance: {
      type: String,
      default: '0',
    },

    // balance after excluding gift cards
    walletUsableBalance: {
      type: String,
      default: '0',
    },
  },
  data() {
    // calc initial wallet amount (string values)
    // have to use usable balance as gift cards are excluded
    let walletAmount = this.walletUsableBalance

    if (compareFloats(this.walletUsableBalance, '>', this.total)) {
      walletAmount = this.total
    }

    // match payload of step 2

    return {
      // track billing vs payment step
      step: 'billing',

      // step 1 manages this data
      // if step 1 is auto-submitted, then it will be populated as if step 1 was
      // filled out based in initialBillingAddress prop
      billingAddress: null as any,

      // payment form
      formData: {
        test: '',
        test2: '',
        useWallet: compareFloats(this.walletUsableBalance, '>', '0.00'),
        walletAmount: walletAmount,
        billingAddressEncoded: '',
        nonce: '',
      },
      processing: false,
      braintreeInstance: null as any,
      paymentInfoError: '',
    }
  },

  /* computed */
  computed: {
    ...mapState(useUserAuthStore, ['user']),

    // powers main section header
    // when step changes, he h1 with this property will be focused
    sectionTitle() {
      // step 2 OR auto-submitting step 1 (to avoid title and focus change)
      if (this.step === 'payment' || (this.initialBillingAddress && !this.billingAddress)) {
        return 'Payment Information'
      }

      // step 1
      return 'Billing Information'
    },

    // check to see if string balance is truthy
    hasWalletBalance() {
      return !!Number.parseFloat(this.walletUsableBalance)
    },

    // if user has wallet balance more than total and usuable wallet amount is
    // less than total, then show a message to the user about how gift cards
    // are excluded
    cannotUseFullBalance() {
      if (
        compareFloats(this.walletBalance, '>', this.maxWalletAmount) &&
        compareFloats(this.total, '>', this.maxWalletAmount)
      ) {
        return true
      }

      return false
    },

    hasOrderBalance() {
      return !!Number.parseFloat(this.orderBalance)
    },

    // return total or usable balance if balance is less than total
    maxWalletAmount() {
      if (compareFloats(this.walletUsableBalance, '<=', this.total)) {
        return this.walletUsableBalance
      }

      return this.total
    },

    // default to order total, but if wallet is used, remaining order balance
    orderBalance() {
      // no balance, full order total
      // if user not using wallet, full total
      // if wallet amount is falsy, full order total
      if (!this.hasWalletBalance || !this.formData.useWallet) {
        return this.total
      }

      // assume user is attempting to use wallet balance
      const walletAmount = Number.parseFloat(`${this.formData.walletAmount}` || '0.00')
      const total = Number.parseFloat(this.total)

      // should never be more than order total
      const balance = total - walletAmount
      return balance.toFixed(2)
    },

    submittingLabel() {
      if (!this.braintreeInstance) {
        return 'Loading...'
      }

      return 'Processing...'
    },
  },

  /* watchers */
  watch: {
    // focus the section header on step change for accessibility
    sectionTitle(newTitle, prevTitle) {
      if (newTitle !== prevTitle) {
        if (this.$refs.sectionHeader instanceof HTMLElement) {
          this.$refs.sectionHeader.focus()
        }
      }
    },

    // scroll on payment error
    paymentInfoError(newError, prevError) {
      if (!newError) {
        return
      }

      if (newError === prevError) {
        return
      }

      // must do nextTicket as callout component that contains error is in v-if
      this.$nextTick(() => {
        // @ts-expect-error this.$refs.paymentError is a vue component so $el is set
        if (this.$refs && this.$refs.paymentError && this.$refs.paymentError.$el) {
          scrollToElemTop({
            // @ts-expect-error this.$refs.paymentError is a vue component so $el is set
            elem: this.$refs.paymentError.$el,
            ms: 250,
          })
        }
      })
    },
  },

  /* lifecycle */
  created() {
    initExtraValidationRules()
  },

  /* component methods */
  methods: {
    goToBillingStep() {
      this.step = 'billing'
    },

    // cancel on the billing step
    handleBillingCancel() {
      window.location.href = `/store/cancel/${this.purchaseId}`
    },

    // update billing address information from step 1 submit
    // OR step1 auto submit with initialBillingAddress pre-filled
    handleBillingSubmit(billingAddress: any) {
      this.billingAddress = billingAddress
      this.step = 'payment'
    },

    // use for display
    moneyFormat(val: string) {
      return moneyFormat(val)
    },

    // handle child event emitter
    onDropInLoad(dropIn: any) {
      this.braintreeInstance = dropIn
    },

    // the customer canceled the 3D Secure challenge modal
    customerCanceled() {
      this.paymentInfoError =
        'Card authentication canceled. Please try again or select another payment method.'
    },

    /**
     * do not allow formData.walletAmount to ever be larger than total
     */
    restrictWalletAmount(e: any) {
      // force string, vue3 converts input type number to string
      const newAmount = `${e.target.value}`
      const maxWalletAmount = this.maxWalletAmount

      // over total
      if (compareFloats(newAmount, '>', maxWalletAmount)) {
        this.formData.walletAmount = maxWalletAmount
        return
      }

      // negative
      if (compareFloats(newAmount, '<', '0.00')) {
        this.formData.walletAmount = '0.00'
        return
      }

      // do not allow more than two decimal places
      if (newAmount.indexOf('.') !== -1 && newAmount.split('.')[1].length > 2) {
        this.formData.walletAmount = Number.parseFloat(newAmount).toFixed(2)
      }
    },

    /**
     * Handles the follow purchase use cases
     * - FB wallet fully covers amount
     * - FB wallet partially covers amount + braintree
     * - all on braintree
     * @returns {Promise<void>}
     */
    async handleSubmit(validate: any) {
      this.processing = true

      // require billing for any payment method
      if (!this.billingAddress) {
        this.processing = false
        this.goToBillingStep()
        return
      }

      // encoded billing address
      this.formData.billingAddressEncoded = this.billingAddress.jsonEncoded
      // end billing

      // wallet form transaction
      if (this.formData.useWallet) {
        // requires validation
        const results = await validate()

        // invalid form (invalid walletAmount
        if (!results.valid) {
          this.processing = false
          return
        }

        // if no order balance, then can submit without braintree to charge ONLY
        // the FB wallet, no braintree nonce needed
        if (!this.hasOrderBalance) {
          this.formData.nonce = ''

          await this.$nextTick()

          if (this.$refs.form instanceof HTMLFormElement) {
            this.$refs.form.submit()
          }

          return
        }
      }
      // end wallet form transaction

      // if we get here, there is an order balance (regardless of fb wallet usage)
      // and braintree is required

      // if no drop in ui
      if (!this.braintreeInstance) {
        this.processing = false
        return
      }

      try {
        // reset braintree specific error
        this.paymentInfoError = ''

        // 3d secure parameters
        // @see https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#requestPaymentMethod
        // @see https://developer.paypal.com/braintree/docs/guides/3d-secure/overview
        // @see https://braintree.github.io/braintree-web/3.103.0/ThreeDSecure.html#verifyCard
        const threeDSecureParameters = {
          amount: this.orderBalance,
          email: this.user.email || '',

          // parse to plain object from observer
          billingAddress: JSON.parse(JSON.stringify(this.billingAddress.threeDSecure)),

          // digital goods delivered electronically
          additionalInformation: {
            productCode: 'DIG',
            shippingMethod: '05', // Electronic Delivery
            shippingMethodIndicator: '05', // Digital goods
            deliveryTimeframe: '01', //  Electronic delivery
            deliveryEmail: this.user.email || '',
          },
        }

        // braintree generate nonce
        this.braintreeInstance.requestPaymentMethod(
          {
            threeDSecure: threeDSecureParameters,
          },
          (err: any, payload: any) => {
            if (err) {
              // No payment method is available.
              // An appropriate error will be shown in the UI.
              console.error(err)

              if (err.code && err.code === 'THREEDS_CARDINAL_SDK_ERROR') {
                // 3d secure specific, also clearing payment method
                this.paymentInfoError =
                  'Card authentication failed. Please try again or select another payment method.'

                this.braintreeInstance.clearSelectedPaymentMethod()
              } else {
                // default, does not clear payment method
                this.paymentInfoError = 'Payment information is required.'
              }

              // re-enable form for submits
              this.processing = false
              return
            }

            // 3d secure user cancel handling as it is not handled
            // in the callback above, but on a braintreeInstance event
            if (this.paymentInfoError) {
              this.processing = false
              return
            }

            // Add the nonce to the form and submit
            this.formData.nonce = payload.nonce

            this.$nextTick(() => {
              if (this.$refs.form instanceof HTMLFormElement) {
                this.$refs.form.submit()
              }
            })
          },
        )
      } catch (e) {
        // re-enable form for submits
        console.error('err with braintreeInstance.requestPaymentMethod', e)
        this.processing = false
      }
    },
  },
})
</script>
